import { Hidden } from '@material-ui/core';
import React from 'react';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import { RadioTableOption } from '../../../global/components/form-element/radio-table-option';
import { YesNoIndicators } from '../../../global/models/api-model';
import { AnnualEnrollmentProps } from '../../arc/models/arc-epi-props';
import { AEOfferingPlan, AEOffering, AETiers, AEPlanType, AEDefaults, AEDependent, AERelationshipCode, ChangeLabel, AEInfo } from '../models/annual-enrollment-models';
import { getCurrencyFormat } from '../../../global/helpers/format-helpers';
import { ExternalLink } from '../../../global/components/external-link-modal';
import { AnnualEnrollmentRoutes } from '../constants/annual-enrollment-routes';
import { NA } from '../../arc/constants/constant-strings';
import { LabelSettingProps } from '../../../global/models/epi-model';
import { ChildTypes, Dependent } from '../../account-info/features/dependents/dependents-model';
import { DeclinedValue, EmployeeAssistanceOnly } from '../../arc/features/new-hire-enrollment/enrollment/models/coverage-models';

export const retireeMedicalGroupId = '3103';

export function getTierLabel(tier: AETiers, annualEnrollmentProps: AnnualEnrollmentProps, labelSettings?: LabelSettingProps) {
	switch (tier) {
		case AETiers.Single:
			return annualEnrollmentProps?.singleTierLabel || 'Single';
		case AETiers.Family:
			return annualEnrollmentProps?.familyTierLabel || 'Family';
		case AETiers.EmpPlusChild:
			return annualEnrollmentProps?.empPlusChildTierLabel || 'Employee + Child';
		case AETiers.EmpPlusOne:
			return annualEnrollmentProps?.empPlusOneLabel || 'Employee + One';
		case AETiers.EmpPlusMulti:
			return annualEnrollmentProps?.empPlusMultiTierLabel || 'Employee + Children';
		default:
			return labelSettings?.naLabel || NA;
	}
}

////Need to wire up to api later
//function getPensionPaid() {
//    return '($700)';
//}

export function getPlanOption(offeringPlan: AEOfferingPlan, selectedPlan: string, displayTier: AETiers, availableTiers: string[], annualEnrollmentProps: AnnualEnrollmentProps, disabled: boolean, labelSetting: LabelSettingProps, isMspPlan?: boolean): RadioTableOption {
	const tierRates = (
		<>
			{availableTiers?.map(tier => {
				const highlighted = offeringPlan.code === selectedPlan && tier === displayTier;

				return (
					<div key={tier} className={`coverage-table-row-cell tier-cell ${highlighted ? 'tier-cell-highlight' : ''}`}>
						<div className='tier-cell-inner'>
							{highlighted && <ArrowDropDownIcon className='tier-cell-indicator' />}

							<Hidden smUp>
								<p className='input-label tier-cell-item tier-cell-name'>{`${getTierLabel(tier as AETiers, annualEnrollmentProps)}:`}</p>
							</Hidden>
							<div className='tier-cell-item tier-cell-value'>{getCurrencyFormat(offeringPlan?.rates_by_tier[tier], labelSetting, true)}</div>
						</div>
					</div>
				);
			})}
		</>

	);


	return {
		label: offeringPlan?.name,
		value: offeringPlan?.code,
		labelSupplement: offeringPlan?.plan_info_url && <ExternalLink linkUrl={offeringPlan?.plan_info_url} linkIcon={<PictureAsPdfIcon />} linkText={annualEnrollmentProps?.planSummaryLinkText || 'Plan Summary'} />,
		additionalLabelContent: tierRates,
		optionDisabled: disabled,
		isMsp: isMspPlan,
	};
}

function IsMspPlan(code: string, keys: string) {
	return keys?.split(',')?.filter(a => a && code?.includes(a))?.length > 0;
}

function compare(a: AEOfferingPlan, b: AEOfferingPlan, keys: string) {
	if (IsMspPlan(a?.code, keys) && IsMspPlan(b?.code, keys)) {
		return 0;
	}

	if (IsMspPlan(a?.code, keys) && !IsMspPlan(b?.code, keys)) {
		return -1;
	}

	if (!IsMspPlan(a?.code, keys) && IsMspPlan(b?.code, keys)) {
		return 1;
	}
	return 0;
}

export function getOfferingsOptions(offering: AEOffering, selectedPlan: string, displayTier: AETiers, expiredName: string, annualEnrollmentProps: AnnualEnrollmentProps, planType: AEPlanType, disabled: boolean, labelSetting: LabelSettingProps, hasMsp?: boolean) {
	const originalPlans = offering?.plans ?? [];
	let plansToUse: AEOfferingPlan[] = originalPlans;
	let currentSelectedPlan = selectedPlan;

	let meapPresent = false;
	if (AEPlanType.Medical) {
		const meapIndex = originalPlans.findIndex(p => p?.code === EmployeeAssistanceOnly);
		if (meapIndex !== -1) {
			meapPresent = true;
			if (!selectedPlan || selectedPlan === DeclinedValue) { currentSelectedPlan = EmployeeAssistanceOnly; }

			const meapPlan = { ...originalPlans[meapIndex], name: 'Employee Assistance Program only (EAP only) - (MEDICAL COVERAGE DECLINED)' };
			plansToUse = [
				...originalPlans.slice(0, meapIndex),
				...originalPlans.slice(meapIndex + 1),
				meapPlan,
			];
		}
	}

	const offerings = plansToUse
		?.sort((a, b) => compare(a, b, annualEnrollmentProps?.mspCodes))
		?.filter(p => (hasMsp && IsMspPlan(p?.code, annualEnrollmentProps?.mspCodes)) || !IsMspPlan(p?.code, annualEnrollmentProps?.mspCodes))
		.map(plan => getPlanOption(plan, currentSelectedPlan, displayTier, offering.available_tiers, annualEnrollmentProps, disabled, labelSetting, IsMspPlan(plan?.code, annualEnrollmentProps?.mspCodes)));

	if (expiredName) {
		offerings.push({
			label: expiredName,
			value: '-1',
			optionDisabled: true,
			hideRadioInput: true,
			additionalLabelContent: <div className='coverage-table-row-cell'><p className='tier-cell-item-expired'><strong>{annualEnrollmentProps?.noLongerOfferedText}</strong></p></div>,
		});
	}

	if (!meapPresent) {
		offerings?.push({
			label: planType === AEPlanType.Medical ? annualEnrollmentProps?.declineMedicalCoverageLabel : annualEnrollmentProps?.declineDentalCoverageLabel,
			value: DeclinedValue,
		});
	}

	return offerings;
}

export function coverageExpired(planType: AEPlanType, defaults: AEDefaults, isCurrentPlan: boolean) {
	return planType === AEPlanType.Medical ? defaults?.medical_going_away === YesNoIndicators.Yes && isCurrentPlan : defaults?.dental_going_away === YesNoIndicators.Yes && isCurrentPlan;
}

export function getDependentCountByRelationship(selectedPeople: string[], eligibleDependents: AEDependent[], relationshipCodes: AERelationshipCode[]) {
	return selectedPeople.filter(person => eligibleDependents.some(dep => relationshipCodes.includes(dep?.relationship_code) && dep.person?._guid === person))?.length || 0;
}

export function countDependentsByRelationship(eligibleDependents: AEDependent[], relationshipCodes: AERelationshipCode[]) {
	return eligibleDependents?.filter(dep => relationshipCodes.includes(dep?.relationship_code))?.length || 0;
}

export function getRelationshipLabel(code: AERelationshipCode, annualEnrollmentProps: AnnualEnrollmentProps) {
	switch (code) {
		case AERelationshipCode.Child:
			return annualEnrollmentProps?.childRelationLabel;
		case AERelationshipCode.Disabled:
			return annualEnrollmentProps?.disabledChildRelationLabel;
		case AERelationshipCode.StepChild:
			return annualEnrollmentProps?.stepChildRelationLabel;
		case AERelationshipCode.DomesticPartner:
			return annualEnrollmentProps?.domesticPartnerRelationLabel;
		case AERelationshipCode.Spouse:
			return annualEnrollmentProps?.spouseRelationLabel;
		default:
			return '';
	}
}

export function getChangeLabelText(changeLabel: ChangeLabel, annualEnrollmentProps: AnnualEnrollmentProps) {
	switch (changeLabel) {
		case ChangeLabel.Added:
			return annualEnrollmentProps.addedText;
		case ChangeLabel.Removed:
			return annualEnrollmentProps.removedText;
		default:
			return '';
	}
}

export function shouldShowAeHeader(history: any) {
	return history?.location?.pathname?.includes(AnnualEnrollmentRoutes.AnnualEnrollment) && !history?.location?.pathname?.includes(AnnualEnrollmentRoutes.Confirmation);
}

/**
 * AE medical group = 3103 would identify this group.
 * @param AEInfo Annual Enrollment info.
 * @returns 
 */
export function isRetiree(aeInfo: AEInfo): boolean {
	return (aeInfo?.ae_defaults?.medical_group_id === retireeMedicalGroupId);
}

const relationshipCodesDependentAgingOut = [AERelationshipCode.Child, AERelationshipCode.StepChild];

/**
 * dep_aging_out flag 'Y' if child dependent will turn 30 in next calendar year.
 * Flags is calculated exactly as stated above, and do not take into account whether the dependent might be disabled or not.
 * @param dependent Dependent to determine if is aging out.
 * @returns True if dependent has aged out.
 */
export function isDependentAgingOut(dependent: AEDependent): boolean {
	return dependent && relationshipCodesDependentAgingOut.includes(dependent.relationship_code) &&
		dependent?.dep_aging_out === YesNoIndicators.Yes && dependent?.person?.disabled_child_indicator === YesNoIndicators.No;
}

/**
 * dep_aged_out flag 'Y' if child dependent is already aged out.
 * Flags is calculated exactly as stated above, and do not take into account whether the dependent might be disabled or not.
 * @param dependent Dependent to determine if is aging out.
 * @returns True if dependent has aged out.
 */
export function isDependentAgedOut(dependent: AEDependent): boolean {
	return dependent && relationshipCodesDependentAgingOut.includes(dependent.relationship_code) &&
		dependent?.dep_aged_out === YesNoIndicators.Yes && dependent?.person?.disabled_child_indicator === YesNoIndicators.No;
}

const relationshipCodesImputedIncome = [AERelationshipCode.Child, AERelationshipCode.StepChild, AERelationshipCode.Disabled];

const relationshipCodesDomesticPartner = [AERelationshipCode.DomesticPartner];

export function isDomesticPartner(dependent: AEDependent): boolean {
	return dependent && relationshipCodesDomesticPartner.includes(dependent.relationship_code);
}

export const getChildType = (dependent: Dependent) => {
	if (dependent?.relationship_type === ChildTypes.step) {
		return ChildTypes.step;
	}
	if (dependent?.relationship_type === ChildTypes.legal && dependent?.person.adoption_date_child !== '') {
		return ChildTypes.adopted;
	}
	if (dependent?.relationship_type === ChildTypes.legal) {
		return ChildTypes.legal;
	}
	return dependent?.relationship_type;
};

/**
 * Dependent who is a domestic partner or any child => 26 years of age during the current year (as of 12/31).
 * @param dependent Dependent to determine if is imputed.
 * @returns True if dependent meets criteria.
 */
export function isDependentHealthImputedIncome(dependent: AEDependent): boolean {
	return isDomesticPartner(dependent) ||
		(dependent && (dependent.age_this_year >= 26) && relationshipCodesImputedIncome.includes(dependent.relationship_code));
}

export function isPlanSelectionAvailable(plans?: AEOfferingPlan[]): boolean {
	return plans?.length > 0;
}